<template>
  <PrimeDrawer
    v-model:visible="visible"
    :position="props.position"
    :base-z-index="props.baseZIndex"
    :show-close-icon="false"
    :unstyled="false"
    :style="{ width: `${props.width}px` }"
    :pt="passThrough"
  >
    <!-- Own container instead of PrimeVue's header/content/footer, so the header and
         footer stay pinned and only the body scrolls. -->
    <template #container="{ closeCallback }">
      <div
        v-if="props.title"
        class="flex flex-nowrap items-start justify-between gap-m px-2xl pt-2xl pb-m shrink-0"
        v-bind="getPt('header', passThrough)"
      >
        <div class="flex flex-col gap-2xs min-w-0">
          <h2
            class="flex flex-nowrap items-center gap-xs text-title-large text-neutral-text-title"
            v-bind="getPt('title', passThrough)"
          >
            <slot name="title-prefix" />
            <span>{{ props.title }}</span>
          </h2>
          <div
            v-if="props.subtitle || $slots.subtitle"
            class="text-body-base text-neutral-text-subtitle"
            v-bind="getPt('subtitle', passThrough)"
          >
            <slot name="subtitle">{{ props.subtitle }}</slot>
          </div>
        </div>
        <div class="flex flex-nowrap items-center gap-s shrink-0">
          <slot name="header-actions" />
          <MeridiaButton
            type="tertiary"
            size="base"
            icon="close"
            :pt="{ icon: { class: '!icon-m !text-neutral-text-title' } }"
            v-bind="getPt('closeButton', passThrough)"
            @click="closeCallback"
          />
        </div>
      </div>
      <div
        class="flex-1 min-h-0 overflow-y-auto px-2xl pb-2xl"
        v-bind="getPt('body', passThrough)"
      >
        <slot />
      </div>
      <!-- relative z-10 keeps the upward shadow above positioned table cells in the body. -->
      <div
        v-if="$slots.footer"
        class="relative z-10 shrink-0 px-2xl py-m shadow-[0px_-1px_3px_0px_rgba(30,41,59,0.1),0px_-1px_2px_0px_rgba(30,41,59,0.06)]"
        v-bind="getPt('footer', passThrough)"
      >
        <slot name="footer" />
      </div>
    </template>
  </PrimeDrawer>
</template>

<script setup lang="ts">
import { DrawerPosition, type MeridiaDrawerPassThrough } from './types';
import PrimeDrawer from 'primevue/drawer';
import MeridiaButton from '../button/MeridiaButton.vue';
import { usePassThrough } from 'primevue/passthrough';
import { getPt } from '../utils';

const visible = defineModel<boolean>('visible', { default: false });

const props = withDefaults(
  defineProps<{
    /** Header title. Without it no header (and no close button) is rendered. */
    title?: string;
    /** Line under the title; the `#subtitle` slot replaces it for rich text. */
    subtitle?: string;
    /** Panel width in px, capped at the viewport width. */
    width?: number;
    /** Edge the drawer slides in from. */
    position?: `${DrawerPosition}`;
    /** Raises the drawer above non-PrimeVue layers with a high z-index (e.g. the map view). */
    baseZIndex?: number;
    /** PrimeVue `root`/`mask`/`transition` plus the Meridia `header`/`title`/`subtitle`/`closeButton`/`body`/`footer`. */
    pt?: MeridiaDrawerPassThrough;
  }>(),
  { width: 760, position: DrawerPosition.RIGHT, baseZIndex: 0 }
);

defineSlots<{
  /** Body; it is the drawer's scroll container. */
  default(): unknown;
  /** Inline before the title text. */
  'title-prefix'(): unknown;
  /** Rich subtitle; replaces the `subtitle` prop. */
  subtitle(): unknown;
  /** Right side of the header, before the close button. */
  'header-actions'(): unknown;
  /** Pinned footer. Declare it with `<template v-if="…" #footer>` so an empty one is not rendered. */
  footer(): unknown;
}>();

// Only PrimeVue's own sections get defaults here. The Meridia sections keep theirs in the
// template: `getPt` reads `{ ...consumer, ...defaults }`, so a default section here would
// replace the consumer's attributes instead of merging with them.
const passThrough = (usePassThrough(
  props.pt,
  { root: { class: 'max-w-full' } },
  { mergeSections: false, mergeProps: true }
) ?? {}) as MeridiaDrawerPassThrough;
</script>
